/**
 * The Raids half of the Meta page.
 *
 * WHOSE NUMBERS THESE ARE. The PvP half defers to PvPoke's published
 * rankings. Nobody publishes raid rankings that way, so these are computed
 * here from the game's own data — and because they are ours, the page says so
 * and states the terms: level 40, best IVs, against a reference boss. See
 * client/src/lib/raidMeta.ts for the formulas.
 *
 * THE BOSS TYPE IS THE POINT. A raid ranking with no target is barely a
 * ranking: which move an attacker uses, and how hard it lands, both depend on
 * what it is hitting. Picking a boss type re-ranks everything and re-picks
 * every moveset, which is why it sits at the top with the measure rather than
 * being buried with the other filters.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";

import { CaughtSprite } from "@/components/CaughtSprite";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import { useMyPokemonRecords } from "@/lib/myPokemonStore";
import { compareRaidRow } from "@/lib/metaCompare";
import {
  armBattleHubReturn,
  getBattleHubMemory,
  setBattleHubRaid,
} from "@/lib/battleHubMemory";
import { getPageScrollTop } from "@/lib/pageScroll";
import { MetaCompareBox } from "./MetaCompareBox";
import { MetaSearchButton } from "./MetaSearchButton";
import { CompareBlock } from "./PvpMetaPanel";
import {
  NO_RAID_FILTERS,
  RAID_MEASURES,
  RAID_TYPE_ORDER,
  attackerTypes,
  raidMeasureByKey,
  rankRaidAttackers,
  type RaidFilters,
  type RaidMeasureKey,
  type RaidRanked,
} from "@/lib/raidMeta";
import {
  RAID_EVAL_LEVEL,
  RAID_EVAL_IV,
  RAID_META_ATTRIBUTION,
} from "@/lib/generated/raidMeta.generated";
import {
  RAID_TIERS_BIGGEST_FIRST,
  raidTierLabel,
  type RaidBossDto,
} from "@/lib/raidRoster";
import { isPokeType } from "@/lib/types";
import { TypeIcon } from "@/components/TypeIcon";
import { ChipRow, MoveChip, TypeBadge, typeColor, typeLabel } from "./metaChrome";
import { SpineDot } from "@/components/SectionHeadingDot";

interface RaidsResponse {
  tiers?: Partial<Record<string, RaidBossDto[]>>;
}

/** How many rows to render. The roster is over a thousand with shadows. */
const SHOWN = 100;

export function RaidMetaPanel() {
  // Applied values survive a round-trip to a record page (battleHubMemory).
  const memory = useMemo(() => getBattleHubMemory().raid, []);
  const [measureKey, setMeasureKey] = useState<RaidMeasureKey>(memory.measureKey);
  const [filters, setFilters] = useState<RaidFilters>(memory.filters);
  const [compare, setCompare] = useState(memory.compare);
  useEffect(() => {
    setBattleHubRaid({ measureKey, filters, compare });
  }, [measureKey, filters, compare]);

  // "Compare your Pokémon to the meta" (2026-09-10 owner ask, Pro).
  const { tier, openPaywall } = useSession();
  const isPro = hasProAccess(tier);
  const records = useMyPokemonRecords();
  const [, setLocation] = useLocation();
  const comparing = compare && isPro;
  const toggleCompare = () => {
    if (!isPro) {
      openPaywall("meta-yours", "Compare your Pokémon to the meta with Pro");
      return;
    }
    setCompare((c) => !c);
  };
  const openRecord = (id: string) => {
    armBattleHubReturn(getPageScrollTop());
    setLocation(`/my-pokemon/${id}`);
  };

  const measure = raidMeasureByKey(measureKey);
  const types = useMemo(() => attackerTypes(), []);
  const { rows, total } = useMemo(
    () => rankRaidAttackers(filters, measureKey, SHOWN),
    [filters, measureKey],
  );

  // The SAME queryKey the Profile raid panel uses, so this shares its cache
  // rather than opening a second request for the same roster.
  const raidsQ = useQuery<RaidsResponse>({
    queryKey: ["/api/events/raids"],
    staleTime: 30 * 60_000,
  });
  const bosses = useMemo(() => {
    const tiers = raidsQ.data?.tiers ?? {};
    // Biggest first: the boss worth planning for should be the first chip you
    // see, not the 1-star you happened to walk past.
    return RAID_TIERS_BIGGEST_FIRST.flatMap((tier) =>
      (tiers[tier] ?? [])
        // A boss with no typing cannot be ranked against, so it is not offered.
        .filter((b) => (b.types?.length ?? 0) > 0)
        .map((b) => ({ boss: b, tier })),
    );
  }, [raidsQ.data]);

  const set = (patch: Partial<RaidFilters>) => setFilters((f) => ({ ...f, ...patch }));

  // The manual type chips and the boss picker set the SAME target, so picking
  // one clears the other: two live selections would leave the list ranked
  // against something neither control was showing.
  const pickType = (t: string) =>
    set({ bossTypes: t === "all" ? [] : [t], bossLabel: null });
  const pickBoss = (b: RaidBossDto) =>
    filters.bossLabel === b.name
      ? set({ bossTypes: [], bossLabel: null })
      : set({ bossTypes: b.types ?? [], bossLabel: b.name });

  const typeChipValue = filters.bossLabel ? "none" : (filters.bossTypes[0] ?? "all");

  // Deliberately one string — see the note on the <p> below.
  const against = filters.bossLabel
    ? ` against ${filters.bossLabel} (${filters.bossTypes.map(typeLabel).join(" / ")})`
    : filters.bossTypes.length > 0
      ? ` against a ${typeLabel(filters.bossTypes[0])} boss`
      : ", against no particular boss";
  const countLine =
    `Top ${rows.length} of ${total.toLocaleString()}${against}. ` +
    `Level ${RAID_EVAL_LEVEL}, ${RAID_EVAL_IV}/${RAID_EVAL_IV}/${RAID_EVAL_IV}, ` +
    "with the best moveset for the measure you picked.";

  return (
    <>
      {/* Each block draws its OWN spine dot rather than relying on DotRail's
          `data-dotrail-dot`: the rail only reads markers off its direct
          children, and these sections are inside this component, so the markers
          it used to carry were never seen and the whole panel sat dotless
          against the rail. See SpineDot. */}
      <section className="relative v2-neon-card-soft p-3" data-testid="section-raid-filters">
        <SpineDot />
        {/* MEASURE first: "best attacker" is a question with several honest
            answers, and which one you meant changes the whole list. */}
        <div
          className="flex gap-1.5 overflow-x-auto pb-0.5"
          role="tablist"
          aria-label="Rank by"
          data-testid="raid-measure-row"
        >
          {RAID_MEASURES.map((m) => {
            const on = m.key === measureKey;
            return (
              <button
                key={m.key}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setMeasureKey(m.key)}
                data-testid={`raid-measure-${m.key}`}
                className="shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold"
                style={{
                  background: on ? "var(--accent-fill-glow-faint)" : "var(--surface-sunken)",
                  color: on ? "var(--brand-green-on-light)" : "var(--text-tertiary)",
                  border: `1px solid ${on ? "var(--accent-fill-glow)" : "var(--border-subtle)"}`,
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
        <p
          className="mt-1.5 text-[10px] leading-snug px-0.5"
          style={{ color: "var(--text-secondary)" }}
          data-testid="raid-measure-blurb"
        >
          {measure.blurb}
        </p>

        {/* THIS WEEK'S BOSSES. The whole point of a raid ranking is the raid
            you are about to do, and a real boss carries two types where the
            chips below carry one — Ice into Garchomp (Dragon/Ground) is 2.56x,
            not 1.6x, which reorders the list outright. */}
        {bosses.length > 0 && (
          <>
            <p className="mt-2.5 text-[9px] font-black uppercase tracking-wider px-0.5" style={{ color: "var(--text-secondary)" }}>
              Raids right now
            </p>
            <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-0.5" data-testid="raid-boss-picker">
              {bosses.map(({ boss, tier }) => {
                const on = filters.bossLabel === boss.name;
                return (
                  <button
                    key={`${tier}-${boss.name}`}
                    type="button"
                    aria-pressed={on}
                    onClick={() => pickBoss(boss)}
                    data-testid={`raid-boss-${(boss.speciesId ?? boss.name).toString().toLowerCase()}`}
                    title={`${raidTierLabel(tier)} · ${(boss.types ?? []).map(typeLabel).join(" / ")}`}
                    className="shrink-0 rounded-full pl-1 pr-2.5 py-1 inline-flex items-center gap-1.5"
                    style={{
                      background: on ? "var(--accent-fill-glow-faint)" : "var(--surface-sunken)",
                      border: `1px solid ${on ? "var(--accent-fill-glow)" : "var(--border-subtle)"}`,
                    }}
                  >
                    {/* 26, not 20: official artwork carries a lot of
                        transparent padding, so a smaller sprite renders the
                        creature as a speck and the chip reads as text only. */}
                    {typeof boss.speciesId === "number" && (
                      <CaughtSprite id={boss.speciesId} size={26} />
                    )}
                    <span
                      className="text-[10.5px] font-bold whitespace-nowrap"
                      style={{ color: on ? "var(--brand-green-on-light)" : "var(--text-secondary)" }}
                    >
                      {boss.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <p className="mt-2.5 text-[9px] font-black uppercase tracking-wider px-0.5" style={{ color: "var(--text-secondary)" }}>
          Boss type
        </p>
        <ChipRow
          testId="raid-boss"
          allLabel="Any"
          options={RAID_TYPE_ORDER.map((t) => ({
            key: t,
            label: typeLabel(t),
            color: typeColor(t),
            icon: isPokeType(t) ? <TypeIcon type={t} size={13} title="" /> : undefined,
          }))}
          value={typeChipValue}
          onPick={pickType}
        />

        <p className="mt-2 text-[9px] font-black uppercase tracking-wider px-0.5" style={{ color: "var(--text-secondary)" }}>
          Attacker type
        </p>
        <ChipRow
          testId="raid-attacker"
          options={types.map((t) => ({
            key: t,
            label: typeLabel(t),
            color: typeColor(t),
            icon: isPokeType(t) ? <TypeIcon type={t} size={13} title="" /> : undefined,
          }))}
          value={filters.attackerType}
          onPick={(attackerType) => set({ attackerType })}
        />

        <button
          type="button"
          onClick={() => set({ includeShadow: !filters.includeShadow })}
          aria-pressed={filters.includeShadow}
          data-testid="raid-shadow-toggle"
          className="mt-2.5 rounded-full px-2.5 py-1 text-[10px] font-bold"
          style={{
            background: filters.includeShadow ? "hsl(280 70% 62% / 0.18)" : "var(--surface-sunken)",
            color: filters.includeShadow ? "hsl(280 70% 62%)" : "var(--text-tertiary)",
            border: `1px solid ${filters.includeShadow ? "hsl(280 70% 62%)" : "var(--border-subtle)"}`,
          }}
          title="Shadows trade 20% more damage taken for 20% more attack, which is strongly positive in raids"
        >
          {filters.includeShadow ? "Shadows included" : "Shadows hidden"}
        </button>
      </section>

      <CompareBlock on={comparing} locked={!isPro} onToggle={toggleCompare} testId="raid-compare-toggle" />

      <section className="relative mt-3" data-testid="section-raid-list">
        <SpineDot />
        {/* One text node, keyed on itself — see the same note in PvpMetaPanel.
            This one was worse: six adjacent text nodes, of which a boss filter
            rewrites the middle three. */}
        <p
          key={countLine}
          className="text-[11px] mb-2 px-1"
          style={{ color: "var(--text-secondary)" }}
          data-testid="raid-count"
        >
          {countLine}
        </p>

        {rows.length === 0 ? (
          <p
            className="v2-neon-card-soft p-4 text-[11.5px]"
            style={{ color: "var(--text-secondary)" }}
            data-testid="raid-empty"
          >
            Nothing matches that combination. Clear a filter and try again.
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="raid-list">
            {rows.map((r) => (
              <li key={`${r.attacker.dexId}-${r.attacker.form ?? ""}-${r.shadow}`}>
                <RaidRow row={r} measureKey={measureKey} targeted={filters.bossTypes.length > 0} />
                {comparing && (
                  <MetaCompareBox
                    rankWidth="w-6"
                    compare={compareRaidRow(records, r)}
                    testId={`raid-compare-${r.attacker.dexId}${r.attacker.form ? `-${r.attacker.form.replace(/\s+/g, "-").toLowerCase()}` : ""}${r.shadow ? "-shadow" : ""}`}
                    onOpen={openRecord}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* These are OUR numbers. Saying so, and saying what they were computed
          from, is the difference between a ranking and an assertion. */}
      <p
        className="mt-4 mb-2 text-[10px] px-1 leading-relaxed"
        style={{ color: "var(--text-secondary)" }}
        data-testid="raid-attribution"
      >
        Computed by Nexus Dex from the{" "}
        <a href={RAID_META_ATTRIBUTION.sourceUrl} target="_blank" rel="noreferrer" className="underline">
          {RAID_META_ATTRIBUTION.source}
        </a>{" "}
        : base stats and the PvE move set, on a full damage cycle (the fast moves
        needed to fund one charged move, plus the charged move). Rating weights
        damage three-to-one against staying alive. Snapshot taken{" "}
        {RAID_META_ATTRIBUTION.generatedOn}.
      </p>
    </>
  );
}

/** How each measure reads. A DPS of 30.4 and a TDO of 812 want different precision. */
function formatValue(row: RaidRanked, measure: RaidMeasureKey): string {
  const v = row.value;
  if (measure === "dps" || measure === "er") return v.toFixed(1);
  return Math.round(v).toLocaleString();
}

function RaidRow({
  row,
  measureKey,
  targeted,
}: {
  row: RaidRanked;
  measureKey: RaidMeasureKey;
  /** True when a boss (or boss type) is set, so the lit badge means something. */
  targeted: boolean;
}) {
  const a = row.attacker;
  const testId = `raid-row-${a.dexId}${a.form ? `-${a.form.replace(/\s+/g, "-").toLowerCase()}` : ""}${row.shadow ? "-shadow" : ""}`;
  return (
    <div className="v2-neon-card-soft px-3 py-2.5 flex items-center gap-3" data-testid={testId}>
      <span
        className="w-6 shrink-0 text-center text-[12px] font-black tabular-nums"
        style={{ color: "var(--text-ink-bright)" }}
      >
        {row.rank}
      </span>

      <CaughtSprite id={a.dexId} size={40} />

      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
          {row.name}
        </p>
        {/* The moveset is the actionable half: the ranking says who, this says
            what to build — and it is chosen FOR the measure on screen. Pills in
            the move's type colour, matching the compare box beneath. */}
        <div className="mt-1 flex flex-wrap gap-1">
          {row.fast && row.charged ? (
            <>
              <MoveChip name={row.fast.name} type={row.fast.type} />
              <MoveChip name={row.charged.name} type={row.charged.type} />
            </>
          ) : (
            <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>No usable moveset</span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {row.shadow && (
            <span
              className="text-[8px] font-black uppercase tracking-wide px-1.5 py-px rounded-full"
              style={{ background: "hsl(280 70% 62% / 0.18)", color: "hsl(280 70% 62%)" }}
              title="+20% attack, +20% damage taken"
            >
              Shadow
            </span>
          )}
          {a.types.map((t) => (
            <TypeBadge
              key={t}
              type={t}
              // A move's type is what the boss resists, so the badge lights when
              // the attacker's own typing is what it is hitting with.
              lit={targeted && row.fast?.type === t}
              testId={`raid-type-${a.dexId}-${t}`}
            >
              {typeLabel(t)}
            </TypeBadge>
          ))}
        </div>
      </div>

      <MetaSearchButton
        row={{ dexId: a.dexId, shadow: row.shadow, mega: a.form?.startsWith("Mega") ?? false }}
        label={row.name}
        testId={`${testId}-search`}
      />

      <span className="shrink-0 text-right">
        <span
          className="block text-[13px] font-black tabular-nums leading-none"
          style={{ color: "var(--accent-fill)" }}
          data-testid={`${testId}-value`}
        >
          {formatValue(row, measureKey)}
        </span>
        <span className="block text-[8px] font-bold uppercase tracking-wide mt-0.5" style={{ color: "var(--text-secondary)" }}>
          {raidMeasureByKey(measureKey).label}
        </span>
      </span>
    </div>
  );
}
